import { Button, Divider, Drawer, Icon, Input, message } from 'antd';
import Form, { FormComponentProps } from 'antd/lib/form';
import * as React from 'react';
import injectSheet from 'react-jss';
import { buttonStyle } from 'src/Library/CommonStyles';
import { StylesProps } from 'src/Library/Types';
import { postBroadcastMessage } from 'src/api/inquiry';
import { tokens } from 'src/design-system';

const styles = () => ({
  customDrawer: {
    '& .ant-drawer-close': {
      left: 0,
      right: 'unset',
      top: '5px',
      fontSize: `${tokens.fontSize.h1}px`,
      color: 'black',
      '& svg': {
        width: '24px',
        height: '24px',
      },
    },
    '& .ant-drawer-title': {
      marginLeft: '30px',
      fontWeight: 500,
      fontSize: `${tokens.fontSize.h3}px`,
    },
    '& .ant-drawer-header': {
      borderBottom: '1px solid #CCCCCC',
    },
    '& .ant-drawer-body': {
      padding: '30px',
    },
    '& .ant-drawer-content-wrapper': {
      minWidth: '500px',
    },
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  modalTitle: {
    fontWeight: tokens.fontWeight.semibold,
    fontSize: `${tokens.fontSize.h2}px`,
    marginLeft: '16px',
  },
  closeIcon: {
    cursor: 'pointer',
    fontSize: `${tokens.fontSize.h1}px`,
  },
  submitButton: {
    ...buttonStyle(tokens.colors.bgWhite, tokens.colors.primaryDark, '1px solid #006EC3'),
    padding: '0px 10px',
    borderRadius: `${tokens.radius.md}px`,
    marginLeft: '28px',
  },
  submitButtonDisabled: {
    ...buttonStyle(tokens.colors.bgWhite, '#CCCCCC', '1px solid #CCCCCC'),
    padding: '0px 10px',
    borderRadius: `${tokens.radius.md}px`,
    marginLeft: '28px',
    cursor: 'not-allowed',
  },
  inputBoxNote: {
    display: 'flex',
    alignItems: 'baseline',
    columnGap: '10px',
    lineHeight: '30px',
    '& span': {
      fontSize: `${tokens.fontSize.caption}px`,
      '&:first-child': {
        fontWeight: `${tokens.fontWeight.semibold}`,
        fontSize: `${tokens.fontSize.body}px`,
        lineHeight: '20px',
      },
      '&:second-child': {
        lineHeight: '14px',
      },
    },
  },
  messageNote: {
    color: '#666',
    fontFamily: 'Open Sans',
    fontSize: `${tokens.fontSize.caption}px`,
    fontStyle: 'italic',
    fontWeight: tokens.fontWeight.regular,
    lineHeight: 'normal',
    marginTop: '20px',
  },
  inquiryListCount: {
    fontSize: `${tokens.fontSize.small}px`,
    fontWeight: `${tokens.fontWeight.semibold}`,
    color: '#111',
    paddingBottom: '20px',
  },
  inquiryDetailsBlock: {
    color: '#111',
    fontSize: `${tokens.fontSize.small}px`,
    fontStyle: 'normal',
    fontWeight: tokens.fontWeight.regular,
    lineHeight: 'normal',
    '& #portDetails': {
      display: 'flex',
      columnGap: '10px',
    },
  },
  inquiryId: {
    color: '#666',
    fontSize: `${tokens.fontSize.caption}px`,
    fontStyle: 'italic',
    fontWeight: tokens.fontWeight.regular,
    lineHeight: 'normal',
  },
});

interface Props extends StylesProps<ReturnType<typeof styles>>, FormComponentProps {
  isVisible: boolean;
  onClose: (flag: boolean) => void;
  selectedInquiry: any;
}

const BroadcastMessage: React.FC<Props> = (props) => {
  const { isVisible, onClose, classes, form, selectedInquiry } = props;

  const [isLoading, setIsLoading] = React.useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = React.useState(true);

  const currentInputTextCount = form.getFieldValue('broadcastMessageInput')?.length || 0;

  const handleMessageInput = () => {
    setIsSubmitDisabled(currentInputTextCount < 1);
  };

  const handleSubmit = async () => {
    const payload = [] as { sourceObjectId: string; text: string; rawHtml: string }[];

    Object.keys(selectedInquiry).forEach((inquiryId) => {
      payload.push({
        sourceObjectId: inquiryId,
        text: form.getFieldValue('broadcastMessageInput'),
        rawHtml: form.getFieldValue('broadcastMessageInput'),
      });
    });
    setIsLoading(true);
    const response = await postBroadcastMessage(payload);
    setIsLoading(false);
    if (response.isSuccess) {
      message.success('Message sent successfully');
      onClose(true);
    } else {
      message.error(response.errorMessage || 'Failed to send message');
    }
  };

  const renderTitle = () => {
    return (
      <div className={classes.modalHeader}>
        <div className={classes.modalTitle}>Broadcast Message</div>
        <Button
					type='primary'
          disabled={currentInputTextCount < 1 || isLoading}
          onClick={() => {
            if (isSubmitDisabled) return;
            handleSubmit();
          }}
          loading={isLoading}
        >
          Send
        </Button>
      </div>
    );
  };

  const renderTextArea = () => {
    return (
      <div>
        <div className={classes.inputBoxNote}>
          <span>Message</span>
          <span>Broadcast message to all participating bidders of the selected inquiries</span>
        </div>
        <div>
          {form.getFieldDecorator('broadcastMessageInput', {
            rules: [{ required: true, message: 'Please enter your message' }],
          })(
            <Input.TextArea
							minLength={1}
							maxLength={1000}
              rows={6}
              placeholder="Type here..."
              allowClear
              onChange={handleMessageInput}
            />
          )}
        </div>
        <div style={{ textAlign: 'right', fontSize: `${tokens.fontSize.caption}px` }}> {currentInputTextCount} / 1000</div>
      </div>
    );
  };

  const renderParticipants = () => {
    const inquiryLength = Object.keys(selectedInquiry).length;
    const inquiryList = Object.values(selectedInquiry) as any[];
    return (
      <div>
        <div className={classes.inquiryListCount}>
          {inquiryLength} {inquiryLength > 1 ? 'Inquiries' : 'Inquiry'} Selected
        </div>
        <div>
          {inquiryList?.map((inquiry, index) => {
            return (
              <div className={classes.inquiryDetailsBlock}>
                <div id="portDetails">
                  <div>{inquiry?.originPortDetails?.name}</div>
                  <div>&rarr;</div>
                  <div>{inquiry?.destinationPortDetails?.name}</div>
                </div>
                <div className={classes.inquiryId}>{inquiry?.referenceNumber}</div>
                {inquiryLength !== index + 1 ? <Divider /> : ''}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Drawer
      width={'600px'}
      visible={isVisible}
      title={renderTitle()}
      destroyOnClose
      maskClosable={false}
      className={classes.customDrawer}
      onClose={() => onClose(false)}
    >
      {renderTextArea()}
      <div className={classes.messageNote}>
        Note: This message will be sent exclusively to participating bidders, i.e., bidders who have
        submitted at least one bid on an inquiry.
      </div>
      <Divider />
      {renderParticipants()}
    </Drawer>
  );
};

export default injectSheet(styles)(Form.create()(BroadcastMessage));
